/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Stack, Box, Button, HStack, Popover, PopoverTrigger, PopoverContent} from '@salesforce/retail-react-app/app/components/shared/ui'
import { HideOnMobile, HideOnDesktop } from '@salesforce/retail-react-app/app/components/responsive'
import { InfoCircle } from '../../components/icons'
import { Tooltip } from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import Field from '../../components/field'
import useLoginFields from '../../components/forms/useLoginFields'

const LoginFields = ({
    form,
    handleForgotPasswordClick,
    prefix = '',
    hideEmail = false,
    hidePassword = false,
    rememberme = false,
    isLoginPage = false
}) => {
    const fields = useLoginFields({form, prefix})
    const {formatMessage} = useIntl()
    return (
        <Stack spacing={2}>
            {!hideEmail && <Field {...fields.email} isLoginPage={isLoginPage} />}
            {!hidePassword && (
                <>
                    <Field {...fields.password} isLoginPage={isLoginPage} />
                    <HStack justifyContent="space-between" alignItems="center">
                        {!rememberme && (
                            <HStack spacing="2" alignItems="center">
                                <Field
                                    {...fields.rememberme}
                                    isLoginPage={isLoginPage}
                                    labelProps={{ fontSize: 'xs' }}
                                    inputProps={{
                                        mt: '3px',
                                        sx: {
                                            '.chakra-checkbox__control': {
                                                mt: '3px'
                                            }
                                        }
                                    }}
                                />
                                <HideOnMobile
                                    sx={{
                                        '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                                            mt: '-1px'
                                        }
                                    }}
                                >
                                    <Tooltip  sx={{inset: '0px auto auto 2rem'}} label={formatMessage({
                                        defaultMessage: `Choosing “Keep me signed in” reduces the number of times you're asked to Sign-In on this device. To ensure the security of your account, use this option only on your personal devices.`,
                                        id: 'use_login_fields.tooltip.rememberme'
                                    })}
                                        bg="#ffffff" color={'inherit'} >
                                        <InfoCircle boxSize="18px" display="block" mt="4px" />
                                    </Tooltip>
                                </HideOnMobile>
                                <HideOnDesktop
                                    sx={{
                                        '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                                            mt: '-1px'
                                        }
                                    }}
                                >
                                    <Popover variant="small" >
                                        <PopoverTrigger>
                                            <InfoCircle boxSize="18px" display="block" />
                                        </PopoverTrigger>
                                        <PopoverContent sx={{inset: '0px auto auto 2rem'}}>
                                            {formatMessage({
                                                defaultMessage: `Choosing “Keep me signed in” reduces the number of times you're asked to Sign-In on this device. To ensure the security of your account, use this option only on your personal devices.`,
                                                id: 'use_login_fields.tooltip.rememberme'
                                            })}
                                        </PopoverContent>
                                    </Popover>
                                </HideOnDesktop>
                            </HStack>
                        )}
                        {handleForgotPasswordClick && (
                            <Box textAlign="right">
                                <Button
                                    variant="link"
                                    onClick={handleForgotPasswordClick}
                                    fontSize="xs"
                                    textDecoration="underline"
                                >
                                    <FormattedMessage
                                        defaultMessage="Forgot password?"
                                        id="contact_info.link.forgot_password"
                                    />
                                </Button>
                            </Box>
                        )}
                    </HStack>
                </>
            )}
        </Stack>
    )
}

LoginFields.propTypes = {
    handleForgotPasswordClick: PropTypes.func,

    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional configurations */
    hideEmail: PropTypes.bool,
    hidePassword: PropTypes.bool,
    rememberme: PropTypes.bool,
    isLoginPage: PropTypes.bool
}

export default LoginFields
